import {
  DestroyRef,
  Directive,
  ElementRef,
  HostBinding,
  OnDestroy,
  ViewContainerRef,
  WritableSignal,
  afterNextRender,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
} from '@angular/core';
import {
  ICONS,
  IUiControlSettings,
  IconType,
  UTILS,
  UiControlSettings,
  StateEnum,
  ROLES,
  PERMISSIONS,
  IStateService,
  STATE_SERVICE_TOKEN,
  IValidationService,
  VALIDATION_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import { UiPermissionDirective } from './ui-permission.directive';

@Directive({
  selector: 'uicontrol',
  standalone: true,
  hostDirectives: [
    {
      directive: UiPermissionDirective,
      inputs: ['uipermission', 'permissionTag'],
    },
  ],
})
export class UiControlDirective implements OnDestroy {
  //#region Injections

  readonly stateService: IStateService = inject(STATE_SERVICE_TOKEN);
  protected readonly validationService: IValidationService = inject(
    VALIDATION_SERVICE_TOKEN,
  );
  public vcRef = inject(ViewContainerRef);
  public hostElRef = inject(ElementRef);
  protected destroyRef = inject(DestroyRef);
  protected permDir = inject(UiPermissionDirective);

  //#endregion

  //#region Inputs/Outputs

  uid = model(UTILS.GENERATE.nextuid.toString());

  settings = input<UiControlSettings, IUiControlSettings>(
    new UiControlSettings(),
    {
      transform: (settings: IUiControlSettings) => this.initSettings(settings),
    },
  );

  state = linkedSignal<StateEnum | undefined>(() => undefined);

  headerCssClass = input<string | undefined>();
  contentCssClass = input<string | undefined>();
  footerCssClass = input<string | undefined>();
  defaultIconCssClass = input<string | undefined>();

  destroyed = output();

  //#endregion

  //#region Host Bindings

  @HostBinding('attr.uicontrol')
  uiControl = '';

  @HostBinding('attr.uifocused')
  get uiFocused() {
    return this.isFocused() ? '' : undefined;
  }

  @HostBinding('attr.uihidden')
  get uiHidden() {
    return this.isHidden() ? '' : undefined;
  }

  @HostBinding('attr.uidisabled')
  get uiDisabled() {
    return this.settings()?.disabled ? '' : undefined;
  }

  @HostBinding('attr.uilabel')
  get uiLabel() {
    return this.label()?.length! > 0 ? this.label() : undefined;
  }

  @HostBinding('attr.uilabelposition')
  get uiLabelPosition() {
    return this.settings()?.labelPosition;
  }

  @HostBinding('attr.uistate') get uiState() {
    return this.state();
  }

  @HostBinding('attr.uitag') get uiTag() {
    return this.tag() ?? this.label() ?? undefined;
  }

  //#endregion

  //#region Properties

  protected readonly PERMISSIONS = PERMISSIONS;
  protected readonly ROLES = ROLES;
  protected readonly ICONS = ICONS;
  protected readonly isNil = UTILS.OBJECT.isNil;

  isFocused: WritableSignal<boolean> = signal<boolean>(false);
  isHidden: WritableSignal<boolean> = signal<boolean>(false);

  label: WritableSignal<string | undefined> = signal(undefined);
  labelFocused: WritableSignal<string | undefined> = signal(undefined);
  labelTip: WritableSignal<string | undefined> = signal(undefined);
  defaultIcon: WritableSignal<IconType | undefined> = signal(undefined);
  tag: WritableSignal<string> = signal('');
  tagGroup: WritableSignal<string> = signal('');
  disabled: WritableSignal<boolean | undefined> = signal(undefined);

  //#endregion

  //#region Functions

  protected initSettings(settings: IUiControlSettings): IUiControlSettings {
    let controlSettings = UTILS.OBJECT.assign(
      new UiControlSettings(),
      settings,
    );
    this.isHidden.set(controlSettings.hide);
    this.label.set(controlSettings.label);
    this.labelFocused.set(controlSettings.labelFocused);
    this.labelTip.set(controlSettings.labelTip);
    this.defaultIcon.set(controlSettings.defaultIcon);
    this.tag.set(controlSettings.tag ?? controlSettings.label);
    this.tagGroup.set(controlSettings.tagGroup);
    if (controlSettings.uid) {
      this.uid.set(controlSettings.uid);
    }
    this.disabled.set(controlSettings.disabled);
    return controlSettings;
  }

  protected focusin() {
    if (this.permDir.uipermission() !== PERMISSIONS.VIEW) {
      this.isFocused.set(true);
    }
  }

  protected focusout() {
    this.isFocused.set(false);
  }

  ngOnDestroy() {
    this.destroyed.emit();
  }

  //#endregion
}
